/**
 * useTeamMembers — single source of truth for the Team page.
 *
 * One team per account, one row per person. Each row carries every workspace
 * the person can enter (`workspaces`, from the server) plus their grant in the
 * workspace the page is standing in (`role`, `enabled`, `permissions`), because
 * that is the grant the page edits. Someone on the roster who was never
 * granted this workspace is still listed — with `onProject: false` — so the
 * owner can see the whole team from any workspace and add people to it from
 * here rather than hunting for them elsewhere.
 *
 * Pending invitations are fetched for every workspace at once and folded into
 * one row per email, so a person invited to two workspaces shows once with two
 * chips, and cancelling can be "this workspace" or "everywhere".
 *
 * Fetched once via `Promise.all` into a normalized, stable `members[]` keyed
 * by id — never by list index.
 */
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import UserService from '@/services/UserService'
import { computeSeatUsage } from '@/Utils/teamPermissionRows'

const lower = (value) => String(value || '').toLowerCase()

/**
 * One workspace a person is on, as the row carries it. Members bring grants
 * (`{ id, name, color, isDefault, role, isEnabled }`); an invitation is shaped
 * the same way so the chips do not care which one they are drawing.
 */
function normalizeWorkspace(raw, project = null) {
	return {
		id: raw.id || raw.workspaceId || project?.id || null,
		name: raw.name || project?.name || 'Workspace',
		color: raw.color || project?.color || '#64748b',
		isDefault: raw.isDefault != null ? !!raw.isDefault : !!project?.isDefault,
		role: raw.role || raw.permissions?.role || 'moderator',
		isEnabled: raw.isEnabled !== false,
		invitationId: raw.invitationId || null,
	}
}

function normalizeMember(raw, { status, permissions, enabled, role, invitedAt, workspaces, onProject } = {}) {
	const perms = permissions || raw.permissions || null
	return {
		_id: raw._id || raw.id || null,
		id: raw.id || raw._id || null,
		name: raw.name || raw.email || '',
		email: raw.email || '',
		role: role || raw.role || perms?.role || 'moderator',
		status: status || raw.status || 'active',
		enabled: enabled != null ? enabled : (raw.enabled != null ? raw.enabled : (perms?.isEnabled !== false)),
		permissions: perms,
		invitedAt: invitedAt || raw.createdAt || raw.created || raw.invitedAt || null,
		workspaces: workspaces || raw.workspaces || [],
		// Whether the person holds a grant (or a pending invitation) in the
		// workspace the page is standing in. The owner is on every workspace.
		onProject: onProject != null ? onProject : true,
	}
}

export function useTeamMembers() {
	const store = useStore()

	const members = ref([])
	const teamName = ref('')
	const tid = ref(null)
	const loading = ref(false)
	const loaded = ref(false)
	const errorMessage = ref('')

	const ownerDetails = computed(() => store.state.User.ownerDetails || [])
	const ownerMembers = computed(() => store.state.User.ownermember || {})
	const subscriptions = computed(() => store.state.User.subscriptions || [])

	/** The workspace whose grants this page lists and edits. */
	const activeProject = computed(() => store.getters['Projects/activeProject'])
	const activeProjectId = computed(() => activeProject.value?.id || null)
	const projectById = (id) => store.getters['Projects/projectById'](id)

	/**
	 * Plan member limit (max invitable members, owner excluded).
	 *
	 * Mirrors `getUserSubscription` in core, including its `parseInt`: the
	 * definition stores this as a string on some packages, and comparing it
	 * strictly against a number read those plans as "no team allowed" — the
	 * dashboard then offered an upgrade for a feature the account already had
	 * and the server would have let it use.
	 *
	 * The allowance is the account's, so it is identical in every workspace.
	 */
	const memberLimit = computed(() => {
		const counts = []
		for (const single of subscriptions.value || []) {
			if (single?.enabled !== true) continue
			if (single?.package?.category === 'scheduled') continue
			const value = single?.definitionOverride?.numOfTeamMembers ||
				single?.package?.definition?.numOfTeamMembers
			const parsed = parseInt(value, 10)
			if (parsed > 0) counts.push(parsed)
		}
		return counts.length ? Math.max(...counts) : 0
	})

	const teamAllowed = computed(() => memberLimit.value > 0)

	/**
	 * `GET /teams/seats`: distinct people across the account, which is how the
	 * server gates invitations. Someone on three workspaces is one seat; so is
	 * someone invited to three. Null until it has answered (or when it fails),
	 * in which case the roster itself is the fallback — it lists every person
	 * once too, so the count is the same, just not authoritative.
	 */
	const accountSeats = ref(null)

	const seatUsage = computed(() => {
		const local = computeSeatUsage(members.value, memberLimit.value)
		const seats = accountSeats.value
		if (!seats) return local
		// The server's limit, not the store's. Both read the same figure off the
		// plan, but the store holds whoever is logged in — and standing in a
		// workspace belonging to someone else, that is the wrong account. Seats
		// and the allowance they are measured against have to come from one
		// place or they describe two different subscriptions.
		const max = Number(seats.limit) || 0
		const occupied = Number(seats.occupied) || 0
		return {
			...local,
			used: Number(seats.active) || 0,
			pending: Number(seats.pending) || 0,
			occupied,
			max,
			atLimit: max > 0 && occupied >= max,
		}
	})

	async function loadAccountSeats() {
		const seats = await UserService.getAccountSeats().catch(() => null)
		accountSeats.value = seats && typeof seats.occupied === 'number' ? seats : null
	}

	/**
	 * The grant a member holds in the active workspace, if any. A payload with no
	 * `workspaces` field at all comes from a server that predates workspaces, where
	 * being on the team meant being on its one workspace; that reads as "here".
	 */
	function grantHere(workspaces) {
		if (!Array.isArray(workspaces)) return { legacy: true, role: undefined, isEnabled: true }
		const id = activeProjectId.value
		if (!id) return workspaces[0] || null
		return workspaces.find((w) => w.id === id) || null
	}

	/**
	 * Pending invitations for every workspace, folded into one row per email.
	 * The row's `permissions`/`role` are the invitation into THIS workspace (the
	 * one the sheet edits and the resend re-sends); the other workspaces only
	 * appear as chips.
	 */
	function pendingRows(invited) {
		const byEmail = new Map()
		for (const raw of invited || []) {
			const key = lower(raw.email)
			if (!key) continue
			// An invitation with no `workspaceId` predates workspaces: it was into
			// the account's one workspace, which is the one the page stands in.
			const invite = { ...raw, workspaceId: raw.workspaceId || activeProjectId.value }
			const project = projectById(invite.workspaceId)
			const workspace = normalizeWorkspace({
				id: invite.workspaceId,
				role: invite.permissions?.role || invite.role,
				invitationId: invite._id || invite.id,
			}, project)
			const existing = byEmail.get(key)
			if (existing) {
				if (!existing.workspaces.some((w) => w.id === workspace.id)) existing.workspaces.push(workspace)
				existing.invites.push(invite)
			} else {
				byEmail.set(key, { invites: [invite], workspaces: [workspace] })
			}
		}

		const rows = []
		for (const { invites, workspaces } of byEmail.values()) {
			const here = activeProjectId.value
				? invites.find((i) => i.workspaceId === activeProjectId.value)
				: invites[0]
			const primary = here || invites[0]
			rows.push(normalizeMember(
				{ ...primary, _id: primary._id || primary.id, id: primary._id || primary.id },
				{
					status: 'pending',
					enabled: true,
					permissions: primary.permissions || null,
					role: primary.role || primary.permissions?.role,
					invitedAt: primary.createdAt || primary.created || primary.invitedAt || null,
					workspaces,
					onProject: !!here || !activeProjectId.value,
				},
			))
		}
		return rows
	}

	/** Roster entries in a stable, scannable order: on this workspace first. */
	function orderRows(memberRows) {
		const here = memberRows.filter((m) => m.onProject)
		const elsewhere = memberRows.filter((m) => !m.onProject)
		return [...here, ...elsewhere]
	}

	function ownerRow(profile, ownerId) {
		return normalizeMember(
			{ id: ownerId, name: profile.name || 'Owner', email: profile.email || '' },
			{ role: 'owner', status: 'active', enabled: true, workspaces: [], onProject: true },
		)
	}

	/**
	 * The member's grant in the active workspace as the list embeds it
	 * (`activeGrant: { workspaceId, role, isEnabled, permissions } | null`),
	 * normalised the way a fetched permission document is. `undefined` means
	 * the server predates the field, and the caller falls back to fetching.
	 */
	function embeddedGrant(m) {
		if (m.activeGrant === undefined) return undefined
		if (!m.activeGrant) return null
		const permissions = m.activeGrant.permissions
			? UserService.normalizeMemberPermission({ ...m.activeGrant.permissions })
			: null
		return {
			role: m.activeGrant.role || permissions?.role || 'moderator',
			isEnabled: m.activeGrant.isEnabled !== false,
			permissions,
		}
	}

	/**
	 * Owner path: `/my-teams` already lists every member with their grants and,
	 * for the active workspace, the full grant (`activeGrant`), so no request is
	 * made per member. The per-member permission call only remains as a
	 * fallback for a server that does not embed it, and then only for people
	 * who hold a grant in this workspace — the others would 404.
	 */
	async function loadOwnerPath() {
		const team = ownerDetails.value[0]
		tid.value = team._id
		teamName.value = team.name || ''
		const activeMembers = Array.isArray(team.members) ? team.members : []

		const ownerProfile = {
			name: store.state.User.name || 'Owner',
			email: store.state.User.email || '',
		}

		const grants = activeMembers.map((m) => grantHere(m.workspaces))
		// `activeGrant` is the grant for whichever workspace the session was in
		// when this payload was fetched, and switching workspace on this page
		// does not refetch it. Once the two have parted company the embedded
		// grant describes a different workspace, so drop it and read the one
		// this page is actually showing instead.
		const embeddedIsHere = !team.activeWorkspaceId || !activeProjectId.value ||
			String(team.activeWorkspaceId) === String(activeProjectId.value)
		const embedded = activeMembers.map((m) => (embeddedIsHere ? embeddedGrant(m) : undefined))
		const [invited, ...permissionResults] = await Promise.all([
			UserService.getInvited(tid.value, 'all').catch(() => []),
			...activeMembers.map((m, i) => (
				embedded[i] === undefined && grants[i]
					? UserService.getMemberpermission(tid.value, m._id, activeProjectId.value).catch(() => null)
					: Promise.resolve(null)
			)),
		])

		const rows = activeMembers.map((m, i) => {
			const here = grants[i]
			const perms = embedded[i] !== undefined ? embedded[i]?.permissions : permissionResults[i]
			const grant = embedded[i] || null
			return normalizeMember(m, {
				status: 'active',
				permissions: perms || (here ? m.permissions || null : null),
				// The role in the workspace on screen first. A member can be an
				// Admin in one and a Moderator in another, and `workspaces` is
				// the only one of these three that is per-workspace.
				role: here?.role || grant?.role || perms?.role || m.role,
				enabled: here
					? (grant ? grant.isEnabled : (perms ? perms.isEnabled !== false : here.isEnabled !== false))
					: false,
				workspaces: (m.workspaces || []).map((w) => normalizeWorkspace(w)),
				onProject: !!here,
			})
		})

		members.value = [
			ownerRow(ownerProfile, team.ownerId),
			...orderRows(rows),
			...pendingRows(invited),
		]
	}

	/**
	 * Member/admin path: the `/teams/info?role=true` payload already carries
	 * every member with `role`/`isEnabled`/`permissions` (and `activeGrant`)
	 * for the active workspace and their `workspaces`. Nothing per member.
	 */
	async function loadMemberPath() {
		const om = ownerMembers.value
		tid.value = om.id || om._id
		teamName.value = om.name || ''

		const invited = await UserService.getInvited(tid.value, 'all').catch(() => [])
		const rows = (om.members || []).map((m) => {
			const here = grantHere(m.workspaces)
			const grant = embeddedGrant(m)
			const perms = grant !== undefined
				? grant?.permissions
				: (m.permissions ? UserService.normalizeMemberPermission({ ...m.permissions }) : null)
			return normalizeMember(m, {
				status: 'active',
				enabled: here
					? (grant ? grant.isEnabled : (m.isEnabled != null ? m.isEnabled !== false : here.isEnabled !== false))
					: false,
				role: grant?.role || m.role || here?.role || perms?.role,
				permissions: perms || null,
				workspaces: (m.workspaces || []).map((w) => normalizeWorkspace(w)),
				onProject: !!here,
			})
		})

		const list = []
		if (om.owner) {
			list.push(ownerRow({ name: om.owner.name, email: om.owner.email }, om.ownerId))
		}
		members.value = [...list, ...orderRows(rows), ...pendingRows(invited)]
	}

	async function load() {
		loading.value = true
		loaded.value = false
		errorMessage.value = ''
		try {
			// Chips need workspace names and colors; the store usually has them by
			// now, but a direct landing on /manage/team must not race it.
			await store.dispatch('Projects/loadProjects').catch(() => {})
			if (ownerDetails.value.length > 0) {
				await loadOwnerPath()
			} else if (ownerMembers.value && (ownerMembers.value.id || (ownerMembers.value.members || []).length)) {
				await loadMemberPath()
			} else {
				members.value = []
			}
			await loadAccountSeats()
			loaded.value = true
		} catch (err) {
			errorMessage.value = err?.message || 'Could not load your team.'
		} finally {
			loading.value = false
		}
	}

	function findById(id) {
		return members.value.find((m) => m._id === id || m.id === id) || null
	}

	function upsertMember(next) {
		const idx = members.value.findIndex((m) => (m._id && m._id === next._id) || (m.id && m.id === next.id))
		if (idx === -1) {
			members.value = [...members.value, next]
		} else {
			const clone = [...members.value]
			clone[idx] = { ...clone[idx], ...next }
			members.value = clone
		}
	}

	function removeById(id) {
		members.value = members.value.filter((m) => m._id !== id && m.id !== id)
	}

	/**
	 * After a one-project removal the server says what the person still holds
	 * (`remainingWorkspaces`) and whether that was their last (`removedFromTeam`).
	 * Mirror that locally rather than refetching the whole roster.
	 */
	function applyProjectRemoval(member, result) {
		const id = member._id || member.id
		if (!result || result.removedFromTeam) {
			removeById(id)
			return
		}
		const remaining = Array.isArray(result.remainingWorkspaces) && result.remainingWorkspaces.length
			? result.remainingWorkspaces.map((w) => normalizeWorkspace(w))
			: member.workspaces.filter((w) => w.id !== activeProjectId.value)
		upsertMember({
			...member,
			workspaces: remaining,
			onProject: false,
			enabled: false,
			permissions: null,
		})
	}

	async function refreshInvited() {
		if (!tid.value) return
		const invited = await UserService.getInvited(tid.value, 'all').catch(() => [])
		const withoutPending = members.value.filter((m) => m.status !== 'pending')
		members.value = [...withoutPending, ...pendingRows(invited)]
		await loadAccountSeats()
	}

	return {
		members,
		teamName,
		tid,
		loading,
		loaded,
		errorMessage,
		memberLimit,
		teamAllowed,
		seatUsage,
		accountSeats,
		activeProject,
		load,
		findById,
		upsertMember,
		removeById,
		applyProjectRemoval,
		refreshInvited,
		refreshSeats: loadAccountSeats,
	}
}
